import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FiMenu,
  FiBell,
  FiLogOut,
  FiSearch,
  FiDatabase,
  FiUser,
  FiStopCircle,
  FiShield,
  FiCalendar,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiFilter,
  FiDownload,
  FiUpload,
  FiRefreshCw,
  FiSettings,
  FiUsers,
  FiMapPin,
  FiArrowLeft,
  FiList,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiHome,
  FiMessageSquare,
  FiHelpCircle,
  FiNavigation,
  FiGlobe,
  FiClock,
  FiCalendar as FiCalendarIcon,
} from 'react-icons/fi';
import logo from '../../../components/assets/logos/logo2.PNG';
import { logout } from '../../redux/Slices/authSlice';
import BaseUrl from '../../Url/BaseUrl';
// Management Action Card Component
const ActionCard = ({ title, description, icon: Icon, color, onClick }) => {
  const { t } = useTranslation();
  return (
    <div className="action-card" onClick={onClick}>
      <div className="action-card__icon-wrapper" style={{ background: color }}>
        <Icon size="2em" />
      </div>
      <h3 className="action-card__title">{t(title) || title}</h3>
      <p className="action-card__description">{t(description) || description}</p>
      <div className="action-card__arrow">→</div>
    </div>
  );
};

// Management Card Component
const ManagementCard = ({ title, count, icon: Icon, color, description, onClick }) => {
  const { t } = useTranslation();
  return (
    <div className="management-card" onClick={onClick}>
      <div className="management-card__header">
        <div className="management-card__icon-wrapper">
          <div className="management-card__icon" style={{ background: color }}>
            <Icon size="1.8em" />
          </div>
          <span className="management-card__count">{count}</span>
        </div>
        <h3 className="management-card__title">{t(title) || title}</h3>
      </div>
      <div className="management-card__content">
        <p className="management-card__description">{t(description) || description}</p>
        <div className="management-card__actions">
          <button className="btn-management btn-view">
            {t('manageNow', 'Manage Now')} →
          </button>
        </div>
      </div>
    </div>
  );
};

// Management Page Layout Component
const ManagementPage = ({ title, description, onBack, children }) => {
  const { t } = useTranslation();
  return (
    <div className="management-page">
      <div className="management-page__header">
        <button className="btn-back" onClick={onBack}>
          <FiArrowLeft /> {t('backToDashboard', 'Back to Dashboard')}
        </button>
        <div className="management-page__title-section">
          <h1>{t(title) || title}</h1>
          <p>{t(description) || description}</p>
        </div>
      </div>
      <div className="management-page__content">
        {children}
      </div>
    </div>
  );
};

// ========== BUS SERVICES MANAGEMENT ==========
const BusServicesManagement = ({ onBack, userRole }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const baseActionCards = [
    {
      id: 1,
      title: 'registerNewBusService',
      titleText: 'Register New Bus Service',
      description: 'addNewBusServiceDescription',
      descriptionText: 'Add a new bus service with route, schedule, and other details',
      icon: FiPlus,
      color: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
      onClick: () => navigate('/bus-finder/bus-services/register')
    },
    {
      id: 2,
      title: 'viewAlterDeleteServices',
      titleText: 'View/Alter/Delete Services',
      description: 'manageExistingBusServicesDescription',
      descriptionText: 'Manage existing bus services, update details or remove services',
      icon: FiList,
      color: 'linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)',
      onClick: () => navigate('/bus-finder/bus-services/manage')
    }
  ];

  const approvalCard = {
    id: 3,
    title: 'approvalManagement',
    titleText: 'Approval Management',
    description: 'approveRejectBusServicesDescription',
    descriptionText: 'Approve or reject pending bus service registrations',
    icon: FiCheckCircle,
    color: 'linear-gradient(135deg, #FF9800 0%, #FF5722 100%)',
    onClick: () => navigate('/bus-finder/bus-services/approvals')
  };

  const actionCards = userRole === 'superadmin' 
    ? [...baseActionCards, approvalCard]
    : baseActionCards;

  return (
    <ManagementPage
      title="busServicesManagement"
      titleText="Bus Services Management"
      description="manageAllBusServicesDescription"
      descriptionText="Manage all bus services, routes, schedules and approvals"
      onBack={onBack}
    >
      <div className="action-cards-grid">
        {actionCards.map((card) => (
          <ActionCard 
            key={card.id} 
            title={card.title}
            description={card.description}
            icon={card.icon}
            color={card.color}
            onClick={card.onClick}
          />
        ))}
      </div>
    </ManagementPage>
  );
};

// ========== BUS OPERATORS MANAGEMENT ==========
const BusOperatorsManagement = ({ onBack, userRole }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const baseActionCards = [
    {
      id: 1,
      title: 'registerNewOperator',
      titleText: 'Register New Operator',
      description: 'registerOperatorDescription',
      descriptionText: 'Register a new bus operator with license and contact details',
      icon: FiUser,
      color: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 100%)',
      onClick: () => navigate('/bus-finder/operators/register')
    },
    {
      id: 2,
      title: 'manageOperators',
      titleText: 'Manage Operators',
      description: 'manageOperatorsDescription',
      descriptionText: 'View, update, or delete operator information',
      icon: FiUsers,
      color: 'linear-gradient(135deg, #FF9800 0%, #FF5722 100%)',
      onClick: () => navigate('/bus-finder/operators/manage')
    }
  ];

  const approvalCard = {
    id: 3,
    title: 'operatorApprovals',
    titleText: 'Operator Approvals',
    description: 'operatorApprovalsDescription',
    descriptionText: 'Approve or reject pending operator registrations',
    icon: FiCheckCircle,
    color: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
    onClick: () => navigate('/bus-finder/operators/approvals')
  };

  const actionCards = userRole === 'superadmin' 
    ? [...baseActionCards, approvalCard]
    : baseActionCards;

  return (
    <ManagementPage
      title="busOperatorsManagement"
      titleText="Bus Operators Management"
      description="manageOperatorsDescriptionMain"
      descriptionText="Manage bus operators, licenses, and approvals"
      onBack={onBack}
    >
      <div className="action-cards-grid">
        {actionCards.map((card) => (
          <ActionCard 
            key={card.id} 
            title={card.title}
            description={card.description}
            icon={card.icon}
            color={card.color}
            onClick={card.onClick}
          />
        ))}
      </div>
    </ManagementPage>
  );
};

// ========== TIMETABLE MANAGEMENT ==========
const TimetableManagement = ({ onBack, userRole }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const actionCards = [
    {
      id: 1,
      title: 'addTimetable',
      titleText: 'Add Timetable',
      description: 'addNewTimetableDescription',
      descriptionText: 'Add new arrival and departure timetables for bus stands',
      icon: FiPlus,
      color: 'linear-gradient(135deg, #36D1DC 0%, #5B86E5 100%)',
      onClick: () => navigate('/bus-finder/timetables/add')
    },
    {
      id: 2,
      title: 'viewTimetables',
      titleText: 'View Timetables',
      description: 'viewManageTimetablesDescription',
      descriptionText: 'View, search and manage all timetables',
      icon: FiList,
      color: 'linear-gradient(135deg, #FF5E62 0%, #FF9966 100%)',
      onClick: () => navigate('/bus-finder/timetables/view')
    }
  ];

  return (
    <ManagementPage
      title="timetableManagement"
      titleText="Timetable Management"
      description="manageBusTimetablesDescription"
      descriptionText="Manage bus arrival and departure timetables at bus stands"
      onBack={onBack}
    >
      <div className="action-cards-grid">
        {actionCards.map((card) => (
          <ActionCard 
            key={card.id} 
            title={card.title}
            description={card.description}
            icon={card.icon}
            color={card.color}
            onClick={card.onClick}
          />
        ))}
      </div>
    </ManagementPage>
  );
};

// ========== ADD TIMETABLE FORM ==========
const AddTimetable = ({ onBack }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [buses, setBuses] = useState([]);
  const [busStands, setBusStands] = useState([]);
  const [formData, setFormData] = useState({
    busStandCode: '',
    busNo: '',
    arrivalTime: '',
    departureTime: '',
    status: true
  });

  // Fetch buses data
  useEffect(() => {
    const fetchBuses = async () => {
      try {
        const response = await fetch(`${BaseUrl.baseurl}Buses`);
        const data = await response.json();
        setBuses(data);
      } catch (error) {
        console.error('Error fetching buses:', error);
        alert('Failed to load buses data');
      }
    };

    // Fetch bus stands data
    const fetchBusStands = async () => {
      try {
        const response = await fetch(`${BaseUrl.baseurl}BusStands`);
        const data = await response.json();
        setBusStands(data);
      } catch (error) {
        console.error('Error fetching bus stands:', error);
        alert('Failed to load bus stands data');
      }
    };

    fetchBuses();
    fetchBusStands();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const formattedData = {
        busStandCode: formData.busStandCode,
        busNo: parseInt(formData.busNo),
        arrivalTime: `${formData.arrivalTime}:00.000Z`,
        departureTime: `${formData.departureTime}:00.000Z`,
        status: formData.status
      };

      const response = await fetch(`${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (response.ok) {
        alert('Timetable added successfully!');
        // Reset form
        setFormData({
          busStandCode: '',
          busNo: '',
          arrivalTime: '',
          departureTime: '',
          status: true
        });
      } else {
        const errorData = await response.json();
        alert(`Failed to add timetable: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding timetable:', error);
      alert('Failed to add timetable. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      busStandCode: '',
      busNo: '',
      arrivalTime: '',
      departureTime: '',
      status: true
    });
  };

  return (
    <ManagementPage
      title="addTimetable"
      titleText="Add Timetable"
      description="addNewTimetableEntry"
      descriptionText="Add new arrival and departure timetable for buses at bus stands"
      onBack={onBack}
    >
      <div className="timetable-form-container" style={{ 
        maxWidth: '800px', 
        margin: '0 auto',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <form onSubmit={handleSubmit}>
          <div className="form-grid" style={{ display: 'grid', gap: '20px' }}>
            {/* Bus Stand Selection */}
            <div className="form-group">
              <label htmlFor="busStandCode" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                Bus Stand *
              </label>
              <select
                id="busStandCode"
                name="busStandCode"
                value={formData.busStandCode}
                onChange={handleSelectChange}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a bus stand</option>
                {busStands
                  .filter(stand => stand.status === true)
                  .map(stand => (
                    <option key={stand.busStandCode} value={stand.busStandCode}>
                      {stand.busStandName} ({stand.busStandCode})
                    </option>
                  ))}
              </select>
            </div>

            {/* Bus Selection */}
            <div className="form-group">
              <label htmlFor="busNo" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                Bus Number *
              </label>
              <select
                id="busNo"
                name="busNo"
                value={formData.busNo}
                onChange={handleSelectChange}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a bus</option>
                {buses
                  .filter(bus => bus.status === true)
                  .map(bus => (
                    <option key={bus.busNo} value={bus.busNo}>
                      {bus.busRegistrationNumber} - {bus.busName} (Bus No: {bus.busNo})
                    </option>
                  ))}
              </select>
            </div>

            {/* Arrival and Departure Times */}
            <div className="form-row" style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <div className="form-group">
                <label htmlFor="arrivalTime" style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Arrival Time *
                </label>
                <input
                  type="time"
                  id="arrivalTime"
                  name="arrivalTime"
                  value={formData.arrivalTime}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="departureTime" style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Departure Time *
                </label>
                <input
                  type="time"
                  id="departureTime"
                  name="departureTime"
                  value={formData.departureTime}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* Status Switch */}
            <div className="form-group">
              <label style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '10px',
                marginBottom: '20px'
              }}>
                <input
                  type="checkbox"
                  checked={formData.status}
                  onChange={handleStatusChange}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Active Status</span>
              </label>
            </div>

            {/* Form Actions */}
            <div className="form-actions" style={{ 
              display: 'flex', 
              gap: '10px',
              marginTop: '20px'
            }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1890ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Adding...' : 'Add Timetable'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </form>
      </div>
    </ManagementPage>
  );
};

// ========== SIDEBAR COMPONENT ==========
const Sidebar = ({ isCollapsed, toggleCollapse }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  
  const menuItems = [
    { id: 1, icon: FiHome, label: t('dashboard', 'Dashboard'), path: '/dashboard' },
    { id: 2, icon: FiSearch, label: t('busFinder', 'Bus Finder'), path: '/busfinderpanel' },
    { id: 3, icon: FiNavigation, label: t('routeFinder', 'Route Finder'), path: '/route-finder-panel' },
    // { id: 4, icon: FiMessageSquare, label: t('notices', 'Notices'), path: '/notices' },
    // { id: 5, icon: FiHelpCircle, label: t('guide', 'Guide'), path: '/guide' },
    // { id: 6, icon: FiSettings, label: t('settings', 'Settings'), path: '/settings' },
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handleMenuItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar__header">
        <button className="sidebar__toggle" onClick={toggleCollapse}>
          <FiMenu size={24} />
        </button>
        {!isCollapsed && <h3 className="sidebar__logo">{t('mainMenu', 'Main Menu')}</h3>}
      </div>

      <nav className="sidebar__menu">
        {menuItems.map((item) => (
          <div
            key={item.id}
            className={`sidebar__item ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => handleMenuItemClick(item)}
            style={{ cursor: 'pointer' }}
          >
            <item.icon size={20} className="sidebar__icon" />
            {!isCollapsed && <span className="sidebar__label">{item.label}</span>}
          </div>
        ))}
      </nav>

      <div className="sidebar__footer">
        <div className="sidebar__item logout" onClick={handleLogout}>
          <FiLogOut size={20} className="sidebar__icon" />
          {!isCollapsed && <span className="sidebar__label">{t('logout', 'Logout')}</span>}
        </div>
      </div>
    </div>
  );
};

// ========== MAIN BUS FINDER PANEL ==========
const BusFinderPanel = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'services', 'operators', 'timetables'
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { t, i18n } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Updated Management Cards Data - Fixed the timetable card title
  const managementCards = [
    {
      id: 1,
      title: 'manageBusServices',
      icon: FiDatabase,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      description: 'manageAllBusRoutesDescription',
      onClick: () => setCurrentView('services')
    },
    {
      id: 2,
      title: 'manageOperatorsCard',
      icon: FiUsers,
      color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      description: 'manageBusOperatorsDescription',
      onClick: () => setCurrentView('operators')
    },
    {
      id: 3,
      title: 'timetableManagement', // Fixed: Changed from 'manageTimetables' to 'timetableManagement'
      icon: FiClock,
      color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      description: 'manageBusTimetablesDescription',
      onClick: () => setCurrentView('timetables')
    }
  ];

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLanguageDropdown(false);
  };

  const Navbar = () => {
    const userName = user.name || user.userName || user.role || t('guestUser', 'Guest User');
    const userRole = user.role ? 
      user.role.charAt(0).toUpperCase() + user.role.slice(1) : 
      t('unknownRole', 'Unknown Role');
    const userStatus = user.email || t('offline', 'Offline'); 
    const isOnline = !!user.email;

    return (
      <header className="navbar">
        <div className="navbar__left">
          <img 
            src={logo} 
            alt={t('logoAlt', 'Rajdoot Logo')}
            style={{
              width: 150,
              height: 70,
              display: 'block',
            }}
          />
        </div>

        <div className="navbar__right">
          {/* Language Switcher */}
          <div className="language-switcher-container" style={{ position: 'relative' }}>
            {/* <button 
              className="navbar__icon-frame language-button"
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '8px',
                borderRadius: '4px',
                transition: 'background-color 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FiGlobe size={24} className="navbar__icon" />
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                {i18n.language === 'en' ? 'EN' : 'HI'}
              </span>
            </button> */}
            
            {/* {showLanguageDropdown && (
              <div 
                className="language-dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  padding: '8px',
                  minWidth: '100px',
                  zIndex: 1000
                }}
              >
                <button 
                  onClick={() => changeLanguage('en')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    textAlign: 'left',
                    background: i18n.language === 'en' ? '#f0f0f0' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '3px',
                    marginBottom: '4px'
                  }}
                >
                  English
                </button>
                <button 
                  onClick={() => changeLanguage('hi')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    textAlign: 'left',
                    background: i18n.language === 'hi' ? '#f0f0f0' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '3px'
                  }}
                >
                  हिन्दी
                </button>
              </div>
            )} */}
          </div>

         
          <div className="navbar__user-profile">
            <div className="navbar__user-avatar">
              <span style={{alignContent:'center'}}>
                {userName.split(' ').map(n => n[0]).join('')}
              </span>
            </div>

            <div className="navbar__user-info">
              <div className="navbar__user-name-role">
                <p className="navbar__user-role">{userRole}</p> 
              </div>
              
              <div className="navbar__user-status">
                {isOnline && <span className="navbar__status-dot"></span>}
                <span className="navbar__status-text">{userStatus}</span>
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  };

  const DashboardView = () => {
    const { t } = useTranslation();
    return (
      <>
        <div className="management-section">
          <div className="management-grid">
            {managementCards.map((card) => (
              <ManagementCard 
                key={card.id} 
                title={card.title}
                count={card.count}
                icon={card.icon}
                color={card.color}
                description={card.description}
                onClick={card.onClick}
              />
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="dashboard-app-container"> 
      <Navbar />
      <div className={`dashboard-content-wrapper ${isCollapsed ? 'collapsed' : ''}`}>
        <Sidebar isCollapsed={isCollapsed} toggleCollapse={toggleCollapse} />
        
        <div className="dashboard__main">
          <div className="dashboard__content">
            {currentView === 'dashboard' && <DashboardView />}
            {currentView === 'services' && (
              <BusServicesManagement 
                onBack={() => setCurrentView('dashboard')} 
                userRole={user.role}
              />
            )}
            {currentView === 'operators' && (
              <BusOperatorsManagement 
                onBack={() => setCurrentView('dashboard')} 
                userRole={user.role}
              />
            )}
            {currentView === 'timetables' && (
              <TimetableManagement 
                onBack={() => setCurrentView('dashboard')} 
                userRole={user.role}
              />
            )}
            {/* Note: For the actual /add and /view routes, you'll need to set up routing in your router */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusFinderPanel;